import { format, isAfter, isBefore, startOfDay } from 'date-fns';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { formatTimeRange, getISOInterval } from 'globals/date-utils';

export type TimeslotLike = {
  startDateTime: string;
  endDateTime: string;
};

export type TimeslotsByPeriod<S> = {
  morning: S[];
  afternoon: S[];
  evening: S[];
};

export type TimeslotsByStartDate<S> = Array<{
  date: string;
  timeslots: TimeslotsByPeriod<S>;
}>;

const isBeforeMidday = (date: Date) => date.getHours() < 12;

const isAfterMiddayButIsNotEvening = (date: Date) =>
  !isBeforeMidday(date) && date.getHours() < 18;

const isAfterMiddayAndIsEvening = (date: Date) => date.getHours() >= 18;

/**
 * Groups timeslots by start date (`yyyy-MM-dd`, ascending) and, within a date,
 * into morning/afternoon/evening buckets ordered by start time.
 */
export const groupTimeslotsByStartDate = <S extends TimeslotLike>(
  timeslots: S[] | null | undefined,
): TimeslotsByStartDate<S> => {
  if (!timeslots?.length) {
    return [];
  }

  const formattedStartDate = (date: string) =>
    format(new Date(date), 'yyyy-MM-dd');

  const groupMap = groupBy(timeslots, t => formattedStartDate(t.startDateTime));

  const orderByStartDateTimeFunc = (map: typeof groupMap, key: string) =>
    orderBy(map[key], t => t.startDateTime, 'asc');

  const groupArray: TimeslotsByStartDate<S> = [];
  for (const groupKey of Object.keys(groupMap)) {
    const morning = orderByStartDateTimeFunc(groupMap, groupKey).filter(d =>
      isBeforeMidday(new Date(d.startDateTime)),
    );
    const afternoon = orderByStartDateTimeFunc(groupMap, groupKey).filter(d =>
      isAfterMiddayButIsNotEvening(new Date(d.startDateTime)),
    );
    const evening = orderByStartDateTimeFunc(groupMap, groupKey).filter(d =>
      isAfterMiddayAndIsEvening(new Date(d.startDateTime)),
    );

    groupArray.push({
      date: groupKey,
      timeslots: {
        morning,
        afternoon,
        evening,
      },
    });
  }

  return orderBy(groupArray, g => g.date, 'asc');
};

/**
 * Given an ascending-sorted list of dates that have available slots and the
 * currently selected date, returns the nearest available date after
 * (nextAvailableDate) and before (previousAvailableDate) the selection, or null
 * when none exists in that direction. Compared by day since availableDates are
 * midnight-parsed while selectedDate may carry a time component.
 */
export const getAdjacentAvailableDates = (
  availableDates: Date[],
  selectedDate: Date,
) => {
  const selected = startOfDay(selectedDate);

  const nextAvailableDate =
    availableDates.find(date => isAfter(startOfDay(date), selected)) ?? null;

  const previousAvailableDate =
    [...availableDates]
      .reverse()
      .find(date => isBefore(startOfDay(date), selected)) ?? null;

  return { nextAvailableDate, previousAvailableDate };
};

type AppointmentTimeDisplayPropsFromData = {
  // https://en.wikipedia.org/wiki/ISO_8601#Time_intervals
  isoInterval: string;
  formattedTime: string;
};

export const formatAppointmentTime = (
  timeSlot: TimeslotLike,
): AppointmentTimeDisplayPropsFromData => {
  const start = new Date(timeSlot.startDateTime);
  const end = new Date(timeSlot.endDateTime);

  return {
    formattedTime: formatTimeRange(start, end),
    isoInterval: getISOInterval(start, end),
  };
};
